import type { PriceTick } from '@/lib/instrumentTick';

/**
 * Pure math for the live price chart engine (no DOM, no chart objects), so the
 * behavioral core is unit-testable. The semantics mirror Polymarket's 5-minute
 * crypto chart (reverse-engineered July 2026):
 *
 * - history is densified with eased sub-second points, not straight segments;
 * - the y-window is the data range clamped to a per-asset minimum span and
 *   padded ±20%, so quiet markets look quiet and the scale never over-zooms;
 * - overlay axis labels sit on "nice" tick steps (1/2/2.5/5/10 × 10ⁿ) with a
 *   per-asset tick-size floor.
 */

/** Cubic ease-in-out — the easing used for seed densification and label slides. */
export function easeInOutCubic(x: number): number {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

/**
 * One exact integration step of a critically damped spring tracking `target` —
 * the live head's motion model.
 *
 * Chosen over the reference's restart-a-fixed-1s-ease-per-tick model because
 * our feed is bursty (see HEAD_TAU_MS in liveChartEngine): position AND
 * velocity are continuous across retargets, so a new tick steers the glide
 * mid-flight instead of resetting it to the ease curve's zero-slope start.
 * From rest the step response has the same character as easeInOutCubic (zero
 * initial slope, soft landing) and, being critically damped, never overshoots.
 *
 * This is the closed-form solution, not a numeric approximation — composing
 * two dt/2 steps equals one dt step, so motion is frame-rate independent.
 */
export function springStep(
  value: number,
  velocity: number,
  target: number,
  dtMs: number,
  tauMs: number,
): { value: number; velocity: number } {
  const omega = 1 / tauMs;
  const x = value - target;
  const decay = Math.exp(-omega * dtMs);
  const temp = (velocity + omega * x) * dtMs;
  return {
    value: target + (x + temp) * decay,
    velocity: (velocity - omega * temp) * decay,
  };
}

/**
 * Densify raw ticks by inserting `perSecond` eased sub-points between each
 * consecutive pair, with fractional timestamps strictly inside the gap. The
 * seed then has the same sub-second density (and curve character) as the live
 * head trail, so the two segments are visually indistinguishable — and hover
 * snapping has near-zero gaps everywhere, like the reference chart.
 */
export function interpolateTicks(raw: readonly PriceTick[], perSecond: number): PriceTick[] {
  if (raw.length === 0) return [];
  const out: PriceTick[] = [];
  for (let i = 0; i < raw.length; i++) {
    const cur = raw[i];
    out.push({ time: cur.time, price: cur.price });
    const next = raw[i + 1];
    if (!next) break;
    const gap = next.time - cur.time;
    if (gap <= 0) continue;
    // Sub-point count scales with the gap so multi-second gaps stay dense.
    const n = Math.max(1, Math.round(perSecond * gap));
    for (let k = 0; k < n; k++) {
      const t = cur.time + ((k + 1) / (n + 1)) * gap;
      const eased = easeInOutCubic(Math.min(k / n, 1));
      out.push({ time: t, price: cur.price + (next.price - cur.price) * eased });
    }
  }
  return out;
}

/** Per-asset minimum visible y-span (dollars). Values match the reference implementation. */
const MIN_SPAN_BY_SYMBOL: Record<string, number> = {
  BTC: 20,
  ETH: 0.8,
  SOL: 0.05,
  XRP: 0.005,
  DOGE: 0.001,
};

/** Fallback minimum span for symbols without an explicit entry: 0.03% of the anchor price. */
const MIN_SPAN_DEFAULT_FRAC = 0.0003;

/**
 * Minimum visible y-span for a symbol. The clamp is what keeps a flat market
 * *looking* flat instead of letting autoscale magnify a $3 wiggle to full pane
 * height — and it lets the Target line legitimately sit off-screen.
 */
export function minSpanFor(symbol: string | null | undefined, anchorPrice: number): number {
  const known = symbol ? MIN_SPAN_BY_SYMBOL[symbol.toUpperCase()] : undefined;
  if (known !== undefined) return known;
  const rel = Math.abs(anchorPrice) * MIN_SPAN_DEFAULT_FRAC;
  return rel > 0 ? rel : 1;
}

/** Fraction of the (clamped) span padded above and below the data range. */
export const AUTOSCALE_PAD_FRAC = 0.2;

/**
 * Post-process an autoscale range: clamp the span to `minSpan`, then pad both
 * sides by {@link AUTOSCALE_PAD_FRAC} of the clamped span. This is the entire
 * vertical "zoom" policy — the chart has no user zoom.
 */
export function padPriceRange(minValue: number, maxValue: number, minSpan: number): { minValue: number; maxValue: number } {
  const span = Math.max(maxValue - minValue, minSpan);
  return {
    minValue: minValue - span * AUTOSCALE_PAD_FRAC,
    maxValue: maxValue + span * AUTOSCALE_PAD_FRAC,
  };
}

/** Price decimals per symbol for axis labels (BTC whole dollars, small-caps more precision). */
export function decimalsFor(symbol: string | null | undefined): number {
  switch (symbol?.toUpperCase()) {
    case 'BTC':
      return 0;
    case 'DOGE':
      return 6;
    case 'XRP':
      return 4;
    default:
      return 2;
  }
}

/**
 * Snap a rough label interval UP to the "nice" family (1 / 2 / 2.5 / 5 / 10 × 10ⁿ),
 * floored at the smallest displayable unit (`1/10^decimals`).
 *
 * Ceiling-biased, exactly like the reference chart's production path: the
 * target label count is a MAXIMUM — a rough interval between nice steps yields
 * fewer labels, never more. (The reference source also carries a floor-biased
 * variant and sub-unit steps for ≥4-decimal assets, but both are dead code in
 * production: no explicit tick count is ever passed, and the normalized ratio
 * is always in [1, 10) so the sub-unit branches are unreachable.)
 */
export function niceStep(rough: number, decimals = 0): number {
  const unit = 1 / Math.pow(10, decimals);
  if (rough < unit) return unit;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const n = rough / mag;
  const snapped = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return Math.max(snapped * mag, unit);
}

/**
 * Compute the overlay axis label prices for a visible range, mirroring the
 * reference chart: inset the range 2% on each side, divide by (targetTicks−1),
 * snap UP with {@link niceStep}, and emit every step covering the inset range
 * (deliberately overshooting one step past each edge — the renderer clips to
 * the pane in pixels).
 */
export function computeLabelPrices(min: number, max: number, targetTicks: number, decimals = 0): number[] {
  const range = max - min;
  if (!(range > 0)) return [];
  const inset = range * 0.02;
  const lo = min + inset;
  const hi = max - inset;
  if (hi <= lo) return [];
  const step = niceStep((hi - lo) / Math.max(targetTicks - 1, 1), decimals);
  const out: number[] = [];
  for (let p = Math.floor(lo / step) * step; p <= hi + step; p += step) {
    if (p >= lo - step && p <= hi + step) out.push(p);
  }
  return out;
}
